/**
 * Step 2 — 도메인 등록. FE-W-05.
 *
 * - 도메인 입력 시 useDomainAvailability hook (debounce 400ms) 으로 indicator
 * - submit → store.setDomainAction → 응답으로 currentStep=2 + dnsChallenge 보관
 * - 409 DOMAIN_RACE → /onboarding/race-rejected navigate
 */

import { useState, useSyncExternalStore, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useOnboardingStore } from '../../../store/onboardingStore';
import { useDomainAvailability } from '../../../hooks/useDomainAvailability';
import { DomainAvailabilityIndicator } from '../shared/DomainAvailabilityIndicator';
import { OnboardingError } from '../../../api/onboarding';
import { tOnb, getOnboardingLang, subscribeOnboardingLang } from '../../../lib/onboarding-i18n';

export function Step2DomainForm() {
  useSyncExternalStore(subscribeOnboardingLang, getOnboardingLang, getOnboardingLang);
  const navigate = useNavigate();
  const setDomainAction = useOnboardingStore((s) => s.setDomainAction);
  const loading = useOnboardingStore((s) => s.loading);
  const storedDomain = useOnboardingStore((s) => s.domain);
  const clearError = useOnboardingStore((s) => s.clearError);
  const error = useOnboardingStore((s) => s.error);

  const [domain, setDomain] = useState<string>(storedDomain ?? '');
  const availability = useDomainAvailability(domain);

  const canSubmit =
    availability.status === 'available' &&
    !loading;

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!canSubmit) return;
    clearError();
    try {
      await setDomainAction({ domain: domain.trim().toLowerCase() });
      navigate('/onboarding/verify');
    } catch (err) {
      if (err instanceof OnboardingError && err.payload.code === 'DOMAIN_RACE') {
        navigate('/onboarding/race-rejected', { replace: true });
        return;
      }
      // 다른 에러는 store.error 로 표시
    }
  }

  return (
    <form onSubmit={(e) => void handleSubmit(e)} noValidate>
      <h2 className="onboarding-step__heading">{tOnb('onboarding.step.label.domain')}</h2>
      <p className="onboarding-step__subheading">
        {tOnb('onboarding.step2.subheading_before_code')} <code>name@&lt;{tOnb('onboarding.step2.label_domain')}&gt;</code> {tOnb('onboarding.step2.subheading_after_code')}
      </p>

      {error && error.code !== 'DOMAIN_RACE' && (
        <div className="onboarding-step__alert" role="alert">
          {error.message}
        </div>
      )}

      <div className="onboarding-step__field">
        <label htmlFor="domain" className="onboarding-step__label">
          {tOnb('onboarding.step2.label_domain')}
        </label>
        <input
          id="domain"
          name="domain"
          type="text"
          className="onboarding-step__input"
          value={domain}
          onChange={(e) => setDomain(e.target.value)}
          placeholder="timesoft.co.kr"
          autoFocus
          autoComplete="url"
          inputMode="url"
          required
          aria-describedby="domain-availability"
        />
        <div id="domain-availability">
          <DomainAvailabilityIndicator state={availability} />
        </div>
      </div>

      <div className="onboarding-step__actions">
        <button
          type="button"
          className="onboarding-step__button onboarding-step__button--secondary"
          onClick={() => navigate('/onboarding')}
        >
          {tOnb('onboarding.btn.prev')}
        </button>
        <button
          type="submit"
          className="onboarding-step__button onboarding-step__button--primary"
          disabled={!canSubmit}
          aria-busy={loading}
        >
          {loading ? tOnb('onboarding.btn.registering') : tOnb('onboarding.btn.next')}
        </button>
      </div>
    </form>
  );
}
